/**
 * Timezone Composable
 *
 * Provides timezone-aware date formatting utilities using company settings
 * Automatically uses the company's configured timezone from Inertia page props
 */

import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import {
    formatInTimezone,
    formatDateInTimezone,
    getTimezoneOffset,
    convertToUTC,
    formatForDateTimeInput,
    getCompanyTimezone,
} from '@/Utils/helpers';

/**
 * Composable for timezone-aware date operations
 * @returns {Object} Timezone utilities
 */
export function useTimezone() {
    const page = usePage();

    /**
     * Get the IANA timezone identifier (e.g., 'Asia/Dubai', 'America/New_York')
     */
    const timezone = computed(() => {
        return page.props.auth?.company?.timezone?.name
            || page.props.app?.timezone
            || 'UTC';
    });

    /**
     * Get the timezone display name (e.g., 'Dubai, Abu Dhabi', 'Eastern Time (US & Canada)')
     */
    const displayName = computed(() => {
        return page.props.auth?.company?.timezone?.display_name
            || 'UTC';
    });

    /**
     * Get the GMT offset string (e.g., 'GMT+4', 'GMT-5')
     */
    const gmtOffset = computed(() => {
        return page.props.auth?.company?.timezone?.gmt_offset
            || 'GMT+0';
    });

    /**
     * Check if DST is observed for this timezone
     */
    const observesDST = computed(() => {
        return page.props.auth?.company?.timezone?.dst_observed ?? false;
    });

    /**
     * Get timezone object with all properties
     */
    const timezoneInfo = computed(() => {
        return page.props.auth?.company?.timezone || {
            name: 'UTC',
            display_name: 'UTC',
            gmt_offset: 'GMT+0',
            dst_observed: false,
        };
    });

    /**
     * Format date using Intl.DateTimeFormat presets
     * @param {Date|string|null} date - Date to format
     * @param {string} preset - Format preset: 'short', 'long', 'date', 'time', 'datetime'
     * @returns {string}
     *
     * Examples:
     * format(date, 'short') → "Jan 15, 2024, 14:30"
     * format(date, 'long') → "January 15, 2024, 14:30:00 GMT+4"
     * format(date, 'date') → "Jan 15, 2024"
     * format(date, 'time') → "14:30"
     */
    const format = (date, preset = 'datetime') => {
        return formatInTimezone(date, preset, timezone.value);
    };

    /**
     * Format date using date-fns format strings
     * @param {Date|string|null} date - Date to format
     * @param {string} formatStr - date-fns format string (e.g., 'MMM dd, yyyy HH:mm')
     * @returns {string}
     *
     * Examples:
     * formatWith(date, 'MMM dd, yyyy') → "Jan 15, 2024"
     * formatWith(date, 'PPpp') → "Jan 15, 2024, 2:30 PM"
     * formatWith(date, 'EEEE, MMMM do, yyyy') → "Monday, January 15th, 2024"
     */
    const formatWith = (date, formatStr = 'MMM dd, yyyy HH:mm') => {
        return formatDateInTimezone(date, formatStr, timezone.value);
    };

    /**
     * Format date for display (common format: Jan 15, 2024, 14:30)
     * @param {Date|string|null} date - Date to format
     * @returns {string}
     */
    const formatDateTime = (date) => {
        return formatWith(date, 'MMM dd, yyyy HH:mm');
    };

    /**
     * Format date only (no time)
     * @param {Date|string|null} date - Date to format
     * @returns {string}
     */
    const formatDate = (date) => {
        return formatWith(date, 'MMM dd, yyyy');
    };

    /**
     * Format time only (no date)
     * @param {Date|string|null} date - Date to format
     * @returns {string}
     */
    const formatTime = (date) => {
        return formatWith(date, 'HH:mm');
    };

    /**
     * Format for datetime-local input field
     * @param {Date|string|null} date - UTC date
     * @returns {string} Format: YYYY-MM-DDTHH:mm
     */
    const formatForInput = (date) => {
        return formatForDateTimeInput(date, timezone.value);
    };

    /**
     * Convert user input to UTC for storage
     * Important: Use this when saving datetime-local input values
     * @param {string|Date} dateInput - Date from input field (in company timezone)
     * @returns {Date} UTC Date object
     *
     * Example:
     * // User inputs "2024-01-15T14:00" in Dubai timezone (GMT+4)
     * const utcDate = toUTC("2024-01-15T14:00");
     * // Returns Date object for "2024-01-15T10:00:00Z" (UTC)
     */
    const toUTC = (dateInput) => {
        return convertToUTC(dateInput, timezone.value);
    };

    /**
     * Get current timezone offset in minutes from UTC
     * Automatically accounts for DST
     * @param {Date} date - Date to check offset for (defaults to now)
     * @returns {number} Offset in minutes (positive = ahead of UTC, negative = behind)
     *
     * Example:
     * offset() // Dubai: 240 (4 hours ahead)
     * offset() // New York winter: -300 (5 hours behind)
     * offset() // New York summer: -240 (4 hours behind, DST)
     */
    const offset = (date = new Date()) => {
        return getTimezoneOffset(timezone.value, date);
    };

    /**
     * Get current time in company timezone
     * @returns {Date}
     */
    const now = () => {
        return new Date();
    };

    /**
     * Check if given date is in DST period for this timezone
     * @param {Date} date - Date to check
     * @returns {boolean}
     */
    const isDST = (date = new Date()) => {
        if (!observesDST.value) return false;

        // Get offset for January (winter) and July (summer)
        const jan = new Date(date.getFullYear(), 0, 1);
        const jul = new Date(date.getFullYear(), 6, 1);

        const janOffset = getTimezoneOffset(timezone.value, jan);
        const julOffset = getTimezoneOffset(timezone.value, jul);

        // DST is when offset is different from winter offset
        const currentOffset = getTimezoneOffset(timezone.value, date);
        return currentOffset !== Math.max(janOffset, julOffset);
    };

    return {
        // Timezone information
        timezone,
        displayName,
        gmtOffset,
        observesDST,
        timezoneInfo,

        // Formatting functions
        format,
        formatWith,
        formatDateTime,
        formatDate,
        formatTime,
        formatForInput,

        // Conversion functions
        toUTC,
        offset,
        now,
        isDST,
    };
}

export default useTimezone;
